import { AuthErrorBoundary } from "@/components/auth/auth-error-boundary";
import { OrganizationNotFoundError } from "@/components/organization-not-found-error";
import { useVersionCheck } from "@/hooks/use-version-check";
import type { AuthClient } from "@/lib/auth";
import { MOBILE_TOAST_OFFSET } from "@/lib/mobile-toast-offset";
import { Toaster } from "@repo/ui";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createRootRouteWithContext<{
  auth: AuthClient;
  queryClient: QueryClient;
}>()({
  component: Root,
});

export function Root() {
  useVersionCheck();

  // Simple URL check using useLocation to ensure reactivity
  // This avoids defining validators at the root level which might affect all routes
  const location = useLocation();

  // Scroll to top on route change to prevent pages opening at previous scroll position
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const searchParams = new URLSearchParams(location.searchStr);
  const isOrgNotFoundError =
    searchParams.get("error") === "organization_not_found";

  if (isOrgNotFoundError) {
    return (
      <AuthErrorBoundary>
        <OrganizationNotFoundError />
        <Toaster mobileOffset={MOBILE_TOAST_OFFSET} />
      </AuthErrorBoundary>
    );
  }

  return (
    <AuthErrorBoundary>
      <Outlet />

      <Toaster mobileOffset={MOBILE_TOAST_OFFSET} />
    </AuthErrorBoundary>
  );
}
